<script setup lang="ts">
  import { useTableProperties } from '@/features/FormBuilder/model/useTableProperties';
  import {
    IconImage,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    Slider,
  } from '@/shared/ui';
  import ConfirmDeleteModal from '@/shared/ui/modals/ConfirmDeleteModal.vue';
  import { Trash2 } from 'lucide-vue-next';
  import { computed, onMounted, ref, watch } from 'vue';
  import { useModal } from 'vue-final-modal';
  import { removeFormBuilderBodyTable } from '../../../composables/removeFormBuilderCanvasElement';
  import BorderExternalIcon from '../../../../../shared/assets/icons/BorderExternalIcon.svg';
  import BorderStyleSingleIcon from '../../../../../shared/assets/icons/BorderStyleSingleIcon.svg';
  import BrushIcon from '../../../../../shared/assets/icons/BrushIcon.svg';
  import DesignIcon from '../../../../../shared/assets/icons/DesignIcon.svg';
  import type { BorderPattern } from '../../../lib/cell-border-utils';
  import { clampNumber, sanitizeColor } from '../../../lib/color-sanitizer';
  import { TABLE_BORDER_DEFAULTS } from '../../../lib/table-constants';
  import type { TableProperties } from '../../../model/form.types';
  import MarginControl from './MarginControl.vue';

  type TableBorderStyle = NonNullable<TableProperties['borderStyle']>;

  interface Props {
    elementId: string;
  }

  const props = defineProps<Props>();

  const {
    element,
    borderWidth,
    tableWidth,
    tableWidthArray,
    tableWidthInput,
    borderPattern,
    borderColor,
    borderStyle,
    padding,
    margin,
    updateProperties,
    handleTableWidthInput,
    handleTableWidthBlur,
  } = useTableProperties(props.elementId);

  const MAX_SPACING = 200;

  const tableWidthInputId = `table-width-input-${props.elementId}`;
  const internalSpacingInputId = `table-internal-spacing-input-${props.elementId}`;

  const borderWidthOptions = ['1', '2', '3', '4', '5'];
  const borderStyleOptions: Array<{ value: TableBorderStyle; label: string }> = [
    { value: 'solid', label: 'Sólida' },
    { value: 'dashed', label: 'Tracejada' },
    { value: 'dotted', label: 'Pontilhada' },
  ];

  const clampSpacing = (value: number) => clampNumber(Math.round(value), 0, MAX_SPACING);

  const spacingInputText = ref(String(0));

  const internalSpacing = computed({
    get: () => padding.value.top ?? 0,
    set: (next) => {
      const normalized = clampSpacing(next);
      updateProperties({
        padding: {
          top: normalized,
          right: normalized,
          bottom: normalized,
          left: normalized,
        },
      });
    },
  });

  const internalSpacingArray = computed({
    get: () => [internalSpacing.value],
    set: (value: number[]) => {
      if (value.length > 0) {
        internalSpacing.value = value[0];
      }
    },
  });

  watch(
    internalSpacing,
    (v) => {
      spacingInputText.value = String(v);
    },
    { immediate: true }
  );

  function onSpacingTextInput(e: Event): void {
    spacingInputText.value = (e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 3);
  }

  function commitSpacingValue(): void {
    const parsed = Number.parseInt(spacingInputText.value, 10);
    if (Number.isNaN(parsed) || spacingInputText.value === '') {
      internalSpacing.value = 0;
      spacingInputText.value = '0';
    } else {
      const clamped = clampSpacing(parsed);
      internalSpacing.value = clamped;
      spacingInputText.value = String(clamped);
    }
  }

  const marginModel = computed({
    get: () => ({
      top: margin.value.top ?? 0,
      right: margin.value.right ?? 0,
      bottom: margin.value.bottom ?? 0,
      left: margin.value.left ?? 0,
    }),
    set: (next: { top: number; right: number; bottom: number; left: number }) => {
      updateProperties({
        margin: {
          top: clampSpacing(next.top),
          right: clampSpacing(next.right),
          bottom: clampSpacing(next.bottom),
          left: clampSpacing(next.left),
        },
      });
    },
  });

  const borderWidthState = computed({
    get: () => String(borderWidth.value),
    set: (next: string) => {
      const parsed = Number.parseInt(next, 10);
      if (Number.isNaN(parsed)) {
        return;
      }
      updateProperties({ borderWidth: clampNumber(parsed, 1, 5) });
    },
  });

  const borderStyleState = computed({
    get: () => borderStyle.value,
    set: (next: TableBorderStyle) => {
      updateProperties({ borderStyle: next });
    },
  });

  const borderColorState = computed({
    get: () => borderColor.value,
    set: (next: string) => {
      updateProperties({ borderColor: sanitizeColor(next, TABLE_BORDER_DEFAULTS.COLOR) });
    },
  });

  const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

  const borderColorHex = ref(borderColorState.value.replace('#', ''));
  const borderColorError = ref('');

  watch(
    () => borderColorState.value,
    (val) => {
      borderColorHex.value = val.replace('#', '');
      borderColorError.value = '';
    }
  );

  function onBorderColorInput(e: Event) {
    const raw = (e.target as HTMLInputElement).value;
    borderColorHex.value = raw;
    const hex = '#' + raw;
    if (HEX_COLOR_REGEX.test(hex)) {
      borderColorError.value = '';
      borderColorState.value = hex;
    } else {
      borderColorError.value = raw.length > 0 ? 'Cor inválida (use formato #RRGGBB)' : '';
    }
  }

  const getBorderStylePreviewClass = (style: TableBorderStyle) => `style-preview--${style}`;

  onMounted(() => {
    if ((borderPattern.value ?? 'all') !== 'all') {
      updateProperties({ borderPattern: 'all' as BorderPattern });
    }
  });

  const handleDelete = () => {
    const isHeader = element.value?.properties.isHeader === true;

    const { open, close } = useModal({
      component: ConfirmDeleteModal,
      attrs: {
        title: isHeader
          ? 'Tem certeza que deseja deletar o cabeçalho?'
          : 'Tem certeza que deseja deletar a tabela?',
        description: 'A ação não pode ser desfeita e as edições feitas serão perdidas.',
        onClose() {
          close();
        },
        onConfirm() {
          close();
          removeFormBuilderBodyTable(props.elementId);
        },
      },
    });

    open();
  };
</script>

<template>
  <div class="section">
    <div class="flex items-center gap-3 border-b border-gray-03 pb-2 pt-6 my-2">
      <div class="bg-gray-03 flex justify-center items-center w-6 h-6 rounded-sm">
        <IconImage
          class="design-icon text-white-01"
          :src="DesignIcon"
          alt="Design"
        />
      </div>
      <h3 class="section-title section-title--header">
        Design
      </h3>
    </div>

    <div class="content-block">
      <h4 class="subsection-title">
        Tabela
      </h4>

      <div class="field-row">
        <Label
          :for="tableWidthInputId"
          class="text-sm font-normal"
        >Largura</Label>
        <div class="relative w-fit">
          <input
            :id="tableWidthInputId"
            v-model="tableWidthInput"
            type="text"
            inputmode="numeric"
            placeholder="100"
            min="1"
            max="100"
            class="number-input"
            aria-label="Largura da tabela em porcentagem"
            @input="
              (event: Event) => handleTableWidthInput((event.target as HTMLInputElement).value)
            "
            @blur="handleTableWidthBlur"
            @keydown.enter="handleTableWidthBlur"
          >
          <span class="suffix">%</span>
        </div>
      </div>

      <Slider
        id="table-width"
        v-model="tableWidthArray"
        :min="1"
        :max="100"
        :step="5"
        class="w-full"
        aria-label="Largura da tabela em porcentagem"
      />

      <div class="divider" />

      <h4 class="subsection-title">
        Espaçamento
      </h4>

      <div class="field-row">
        <Label
          :for="internalSpacingInputId"
          class="text-sm font-normal"
        >Interno</Label>
        <div class="relative w-fit">
          <input
            :id="internalSpacingInputId"
            type="text"
            inputmode="numeric"
            maxlength="3"
            min="0"
            :max="MAX_SPACING"
            :value="spacingInputText"
            class="number-input"
            @focus="($event.target as HTMLInputElement).select()"
            @input="onSpacingTextInput"
            @blur="commitSpacingValue"
            @keydown.enter="commitSpacingValue"
          >
          <span class="suffix">px</span>
        </div>
      </div>

      <Slider
        id="internal-spacing"
        v-model="internalSpacingArray"
        :min="0"
        :max="MAX_SPACING"
        :step="1"
        class="w-full"
        aria-label="Espaçamento interno"
      />

      <MarginControl
        v-model="marginModel"
        :element-id="elementId"
      />

      <div class="divider" />

      <h4 class="subsection-title">
        Bordas
      </h4>

      <div class="border-menus">
        <button
          type="button"
          class="compact-trigger compact-trigger--static"
          data-testid="border-pattern-menu"
          disabled
          aria-label="Borda externa selecionada"
        >
          <IconImage
            :src="BorderExternalIcon"
            alt="Seleção de borda"
          />
        </button>

        <Select v-model="borderWidthState">
          <SelectTrigger
            class="compact-trigger compact-trigger--width"
            data-testid="border-width-menu"
          >
            <IconImage
              :src="BorderStyleSingleIcon"
              alt="Espessura da borda"
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="option in borderWidthOptions"
              :key="option"
              :value="option"
            >
              {{ option }}px
            </SelectItem>
          </SelectContent>
        </Select>

        <Select v-model="borderStyleState">
          <SelectTrigger
            class="compact-trigger compact-trigger--style"
            data-testid="border-style-menu"
          >
            <div
              class="style-preview-stack"
              aria-hidden="true"
            >
              <span class="style-preview style-preview--solid" />
              <span class="style-preview style-preview--dashed" />
              <span class="style-preview style-preview--dotted" />
            </div>
            <span class="sr-only">Estilo de borda atual</span>
          </SelectTrigger>
          <SelectContent
            class="style-select-content !w-[100px] !min-w-[100px] !max-w-[100px] rounded-2xl p-0"
            position="popper"
            side="bottom"
            align="center"
            :side-offset="6"
          >
            <SelectItem
              v-for="option in borderStyleOptions"
              :key="option.value"
              :value="option.value"
              class="style-select-item"
            >
              <div class="style-option-item">
                <span
                  class="style-preview"
                  :class="getBorderStylePreviewClass(option.value)"
                  aria-hidden="true"
                />
                <span class="sr-only">{{ option.label }}</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div class="space-y-2">
        <p class="text-sm font-normal">
          Cor
        </p>
        <div class="flex gap-2 h-[42px] items-center">
          <div class="flex items-center justify-center size-8 shrink-0">
            <IconImage
              :src="BrushIcon"
              alt="Cor da borda"
              class="text-gray-03"
            />
          </div>
          <div
            class="flex-1 flex items-center gap-2 h-[42px] bg-white-01 border border-gray-02 rounded-lg pl-4 pr-3 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] overflow-hidden"
          >
            <div
              class="relative size-6 rounded border border-gray-02 shrink-0 cursor-pointer overflow-hidden"
              :style="{ backgroundColor: borderColorState }"
            >
              <input
                type="color"
                :value="borderColorState"
                class="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                @input="(e) => (borderColorState = (e.target as HTMLInputElement).value)"
              >
            </div>
            <span class="text-gray-03 font-medium text-sm shrink-0">#</span>
            <input
              :value="borderColorHex"
              type="text"
              class="flex-1 text-sm text-gray-07 bg-transparent outline-none border-none min-w-0"
              placeholder="000000"
              @input="onBorderColorInput"
            >
          </div>
        </div>
        <p
          v-if="borderColorError"
          class="text-sm text-red-500 font-medium"
        >
          {{ borderColorError }}
        </p>
      </div>

      <div class="divider" />

      <button
        type="button"
        class="mt-4 flex items-center justify-center gap-2 w-full h-[42px] rounded-lg border border-gray-02 bg-white-01 text-sm font-medium text-gray-07 hover:bg-gray-02 transition-colors"
        @click="handleDelete"
      >
        <Trash2 class="size-5 text-[var(--color-red-16)]" />
        Deletar
      </button>

      <p class="sr-only">
        Largura atual: {{ tableWidth }}%
      </p>
    </div>
  </div>
</template>

<style scoped>
  .section {
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--color-gray-02);
  }

  .section-title {
    font-size: 14px;
    font-weight: 400;
    color: var(--color-gray-07);
    margin-bottom: 16px;
  }

  .section-title--header {
    margin-bottom: 0;
  }

  .content-block {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .subsection-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-gray-11);
  }

  .field-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
  }

  .number-input {
    width: 74px;
    height: 42px;
    border: 1px solid var(--color-gray-02);
    border-radius: 8px;
    background: var(--color-white-01);
    color: var(--color-gray-07);
    font-size: 14px;
    text-align: center;
    padding: 10px 24px 10px 10px;
    outline: none;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    -moz-appearance: textfield;
    appearance: textfield;
  }

  .number-input::-webkit-outer-spin-button,
  .number-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .suffix {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 14px;
    color: var(--color-gray-03);
    pointer-events: none;
  }

  .divider {
    width: 100%;
    height: 1px;
    background: var(--color-gray-02);
    margin-top: 4px;
    margin-bottom: 4px;
  }

  .border-menus {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 6px;
    height: 42px;
  }

  .compact-trigger {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0 6px;
  }

  .compact-trigger--static {
    border: 1px solid transparent;
    border-radius: 6px;
    background: rgba(227, 227, 227, 0.2);
    cursor: default;
  }

  .style-option-item {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0;
  }

  .style-preview-stack {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .style-preview {
    width: 24px;
    border-top-width: 2px;
    border-top-color: #475467;
    border-top-style: solid;
    display: block;
  }

  .style-option-item .style-preview {
    width: 36px;
  }

  .style-preview--solid {
    border-top-style: solid;
  }

  .style-preview--dashed {
    border-top-style: dashed;
  }

  .style-preview--dotted {
    border-top-style: dotted;
  }

  .design-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  .design-icon :deep(svg) {
    width: 19px;
    height: 19px;
    display: block;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
</style>

<style>
  .style-select-content[data-slot='select-content'] {
    min-width: 100px !important;
    width: 100px !important;
  }

  .style-select-content [data-slot='select-viewport'] {
    width: 100px !important;
    min-width: 0 !important;
    padding: 2px 0 !important;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .style-select-item[data-slot='select-item'] {
    height: 34px;
    justify-content: center;
    padding: 0 !important;
    border-radius: 0;
  }

  .style-select-item > span.absolute {
    display: none;
  }

  .style-select-item > [data-slot='select-item-text'] {
    width: 100%;
    display: flex;
    justify-content: center;
  }
</style>
